/**
 * QuickCreateScreen — the one-screen repeat path of create mode (W12 Part B,
 * Task B7).
 *
 * The wizard ALWAYS opens first; quick create is reached only through the
 * explicit header switch and is then remembered per project (owner ruling
 * 2026-09-10). This screen is what that switch shows: the same step-1 body
 * (`AnalysisStep`) with the latest completed analysis already pinned, plus one
 * read-only row per setting — each with a «Change» that opens the wizard step
 * owning it.
 *
 * Props-in / JSX-out. Which analysis is pinned, what a row says and what
 * «Change» opens are all decided by `StrategyFormDialog`; the FOOTER is the
 * review footer, rendered by the dialog as a sibling (exactly as the wizard
 * shell does) so there is only one «Create with AI» implementation.
 */

import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';
import { AnalysisStep, type AnalysisStepProps } from './steps/AnalysisStep';
import {
  StrategySubmitErrorAlert,
  type StrategySubmitError,
} from '../StrategySubmitErrorAlert';
import type { LaunchReadiness } from '../GenerationReadinessStrip';
import type { QuickCreateRow } from './quickCreateRows';

/** The owner's verbatim promise of what this screen does (approved canvas v3.3). */
export const QUICK_CREATE_SUBTITLE =
  'Latest analysis and project defaults — change what you need, then create.'
  + ' This project opens in quick create until you switch back.';

/**
 * The readiness verdict. Worded by `launchReadinessStatus`, which derives the
 * label AND the blocked flag from the SAME gates the launch primary is fed —
 * quick create never invents vocabulary and can never read «Ready to generate»
 * next to a disabled button.
 */
export type QuickCreateReady = LaunchReadiness;

export interface QuickCreateScreenProps {
  /** The SAME step-1 body the wizard renders (Task B2), latest analysis pinned. */
  analysis: AnalysisStepProps;
  rows: QuickCreateRow[];
  /** «Change» on a row — opens the wizard step that owns it. */
  onChange: (key: QuickCreateRow['key']) => void;
  ready: QuickCreateReady;
  /** «Step-by-step instead» — flips the stored mode back to guided. */
  onSwitchToGuided: () => void;
  /**
   * F-LIVE2-40 (fix round 1, review #2): the submit's own failure — a refusal or
   * a «created, but generation failed» — stated on THIS screen. Quick create had
   * no surface for either: the dialog set the message, switched the invisible
   * wizard to its review step and returned, so the operator was told nothing.
   * Same component, same copy as the review step.
   */
  submitError?: StrategySubmitError | null;
}

const ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  py: 1,
  borderTop: 1,
  borderColor: 'divider',
} as const;
const ROW_LABEL_SX = { width: 206, flexShrink: 0 } as const;

/** One setting: label · value (+ detail) · Change. */
const SettingRow: React.FC<{
  row: QuickCreateRow;
  onChange: (key: QuickCreateRow['key']) => void;
}> = ({ row, onChange }) => (
  <Box sx={ROW_SX} data-testid={STRATEGY_LIST.quickCreateRow(row.key)}>
    <Typography variant="body2" color="text.secondary" sx={ROW_LABEL_SX}>{row.label}</Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2">{row.value}</Typography>
      {row.detail && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {row.detail}
        </Typography>
      )}
    </Box>
    <Button
      variant="text"
      size="small"
      onClick={() => onChange(row.key)}
      aria-label={`Change ${row.label}`}
      data-testid={STRATEGY_LIST.quickCreateChange(row.key)}
      // Invariant 15: text-level primary affordances use `primary.dark` on the
      // dialog's off-white paper.
      sx={{ flexShrink: 0, textTransform: 'none', color: 'primary.dark' }}
    >
      Change
    </Button>
  </Box>
);

/**
 * The chip's three tones. Hydration is NEUTRAL, never the error tone: a screen
 * that greets its operator with a red failure chip before its data has arrived
 * is exactly what strategy-blocks designer invariant 5 forbids (Task B9d fix 6).
 */
const readinessChipTone = (ready: QuickCreateReady): {
  icon: React.ReactElement;
  sx: Record<string, string>;
} => {
  if (ready.label === 'Checking…') {
    return {
      icon: <HourglassEmptyIcon />,
      sx: { color: 'text.secondary', borderColor: 'divider' },
    };
  }
  if (ready.blocked) {
    return { icon: <ErrorOutlineIcon />, sx: { color: 'error.main', borderColor: 'error.main' } };
  }
  // Invariant 15: `primary.main` text on the #fafafa paper is 4.41:1 and fails
  // AA, so the chip's LABEL takes `primary.dark`.
  return { icon: <CheckCircleIcon />, sx: { color: 'primary.dark', borderColor: 'primary.main' } };
};

export const QuickCreateScreen: React.FC<QuickCreateScreenProps> = ({
  analysis,
  rows,
  onChange,
  ready,
  onSwitchToGuided,
  submitError = null,
}) => {
  const chip = readinessChipTone(ready);
  return (
    <Box
      data-testid={STRATEGY_LIST.quickCreate}
      // Owner ruling: content is centred in ONE 800px column — the same column
      // the wizard shell uses, so switching modes does not move the page.
      sx={{
        width: '100%',
        maxWidth: 800,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        pt: 3,
        flex: '1 1 auto',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {/* Not a wizard STEP header: no step id, no step guide ⓘ — quick
              create is one screen, and its explanations live on the steps. */}
          <Typography variant="h5" sx={{ flex: '1 1 auto' }}>Create a strategy</Typography>
          <Button
            variant="text"
            size="small"
            onClick={onSwitchToGuided}
            sx={{ textTransform: 'none', color: 'primary.dark', whiteSpace: 'nowrap' }}
            data-testid={STRATEGY_LIST.wizardSwitchMode}
          >
            Step-by-step instead
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">{QUICK_CREATE_SUBTITLE}</Typography>
      </Box>

      {/* Above everything the failure invalidates, and above the footer primary
          that produced it — the same place the review step states it. */}
      <StrategySubmitErrorAlert error={submitError} />

      <AnalysisStep {...analysis} />

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, flex: '1 1 auto' }}>Settings</Typography>
          <Chip
            size="small"
            variant="outlined"
            icon={chip.icon}
            label={ready.label}
            data-testid={STRATEGY_LIST.quickCreateReady}
            sx={chip.sx}
          />
        </Box>
        {rows.map((row) => (
          <SettingRow key={row.key} row={row} onChange={onChange} />
        ))}
      </Box>
    </Box>
  );
};

QuickCreateScreen.displayName = 'QuickCreateScreen';
